import { useEffect, type RefObject } from 'react';

interface IdskModuleConstructor {
  new (element: Element): { init: () => void };
}

const initialized = new WeakSet<Element>();

/**
 * Initializes an ID-SK/GOV.UK JavaScript module (UMD constructor) on the component root element.
 * Guarded with a WeakSet so React StrictMode's double effects (and re-renders) never initialize twice.
 */
export function useIdskModule(ref: RefObject<Element | null>, load: () => Promise<IdskModuleConstructor>): void {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    // StrictMode runs effect → cleanup → effect: only the still-active run initializes, and only once per element.
    let active = true;
    void load().then((Module) => {
      if (!active || !element.isConnected || initialized.has(element)) return;
      initialized.add(element);
      new Module(element).init();
    });
    return () => {
      active = false;
    };
  }, [ref, load]);
}

export function isInitialized(element: Element): boolean {
  return initialized.has(element);
}
